import type { ComponentType } from 'react';
import { matchPath } from 'react-router-dom';
import { SettingsLayout } from '@/features/ai-dart/views/settings/SettingsLayout';
import { TicketDetailView } from '@/features/ai-dart/views/tickets/TicketDetailView';
import { TicketsView } from '@/features/ai-dart/views/tickets/TicketsView';
import { ChecklistView } from '@/features/onboarding/views/ChecklistView';
import { GlossaryView } from '@/features/onboarding/views/GlossaryView';
import { HarnessView } from '@/features/onboarding/views/HarnessView';
import { LinksView } from '@/features/onboarding/views/LinksView';
import { SetupView } from '@/features/onboarding/views/SetupView';
import { StartView } from '@/features/onboarding/views/StartView';
import { BacklogView } from '@/features/project-board/views/BacklogView';
import { DashboardView } from '@/features/project-board/views/DashboardView';
import { DevRequestView } from '@/features/project-board/views/DevRequestView';
import { EpicView } from '@/features/project-board/views/EpicView';
import { IncidentView } from '@/features/project-board/views/IncidentView';
import { IndexSettingsView } from '@/features/project-board/views/IndexSettingsView';
import { IssueView } from '@/features/project-board/views/IssueView';
import { KanbanView } from '@/features/project-board/views/KanbanView';
import { QaView } from '@/features/project-board/views/QaView';
import { AccountView } from '@/features/settings/views/AccountView';
import { ApiTokenView } from '@/features/settings/views/ApiTokenView';
import { AiUsageView } from '@/features/ai-usage/views/AiUsageView';
import { HowtoView } from '@/features/howto/views/HowtoView';
import { ExternalAccountView, InternalInfraAccountView } from '@/features/external-account/views/ExternalAccountView';
import { GrafanaDashboardView } from '@/features/service-dashboard/grafana/GrafanaDashboardView';
import { ServiceMapView } from '@/features/service-dashboard/service-map/ServiceMapView';

/**
 * 메뉴 레지스트리 — **메뉴를 추가하는 유일한 지점**.
 *
 * 어드민에 어떤 기능이 언제 붙을지 모르므로, 화면은 이 선언에서 파생된다: 사이드바 목록, 라우트,
 * 상단 제목이 전부 여기서 나온다. 새 기능은 `section`을 하나 추가하고 그 안에 `items`를 넣으면
 * 라우팅·내비게이션이 따라온다(AppShell·routes는 손대지 않는다).
 *
 * 섹션 하나 = `features/<key>` 디렉토리 하나 = 라우트 접두어 하나. 셋을 같은 이름으로 맞춰 두면
 * 메뉴를 보고 코드 위치를 찾을 수 있고, 다른 섹션 코드가 섞여 들어가면 import 경로에서 바로 드러난다.
 */
export interface NavItem {
  /** 섹션 경로 뒤에 붙는 조각. 빈 문자열이면 섹션 기본 화면. */
  path: string;
  label: string;
  element: ComponentType;
  /** 라우트에는 포함하되 사이드바에는 직접 노출하지 않는다. */
  hidden?: boolean;
  /** 숨은 하위 라우트가 선택됐을 때 활성화할 대표 메뉴 경로. */
  activePath?: string;
  /** 이 화면 하나만 여는 권한 키(`console_menu`의 하위 칸). 없으면 섹션 권한으로만 열린다. */
  menuKey?: string;
}

export interface NavSection {
  /** `features/<key>` 디렉토리 이름과 같아야 한다. */
  key: string;
  label: string;
  /** 사이드바 아이콘 대신 쓰는 한 글자 — 아이콘 의존성을 두지 않는다. */
  glyph: string;
  /** `/project-board` 처럼 이 섹션의 라우트 접두어. */
  basePath: string;
  /** 섹션 전체를 여는 권한 키(`console_menu`의 대메뉴 행). 섹션 `key`와 다를 수 있다(계정 메뉴). */
  menuKey: string;
  items: NavItem[];
}

export const NAV_SECTIONS: NavSection[] = [
  {
    key: 'project-board',
    label: '프로젝트 보드',
    glyph: '◫',
    basePath: '/project-board',
    menuKey: 'project-board',
    items: [
      { path: 'dashboard', label: '대시보드', element: DashboardView, menuKey: 'project-board:dashboard' },
      { path: '', label: '에픽', element: EpicView, menuKey: 'project-board:epics' },
      { path: 'kanban', label: '보드', element: KanbanView, menuKey: 'project-board:kanban' },
      { path: 'backlog', label: '백로그', element: BacklogView, menuKey: 'project-board:backlog' },
      { path: 'issues', label: '이슈', element: IssueView, menuKey: 'project-board:issues' },
      { path: 'qa', label: 'QA', element: QaView, menuKey: 'project-board:qa' },
      { path: 'incidents', label: '인시던트', element: IncidentView, menuKey: 'project-board:incidents' },
      { path: 'dev-requests', label: '개발요청', element: DevRequestView, menuKey: 'project-board:dev-requests' },
      { path: 'settings', label: '설정', element: IndexSettingsView, menuKey: 'project-board:settings' },
    ],
  },
  {
    key: 'ai-dart',
    label: 'AI-DART',
    glyph: '◆',
    basePath: '/ai-dart',
    menuKey: 'ai-dart',
    items: [
      { path: 'tickets', label: '티켓', element: TicketsView },
      { path: 'tickets/:id', label: '티켓 상세', element: TicketDetailView, hidden: true, activePath: 'tickets' },
      { path: 'settings/orchestrators', label: '설정', element: SettingsLayout },
      { path: 'settings/sources', label: '설정 / 소스', element: SettingsLayout, hidden: true, activePath: 'settings/orchestrators' },
      { path: 'settings/repos', label: '설정 / 레포·브랜치', element: SettingsLayout, hidden: true, activePath: 'settings/orchestrators' },
      { path: 'settings/schedules', label: '설정 / 스케줄', element: SettingsLayout, hidden: true, activePath: 'settings/orchestrators' },
      { path: 'settings/ingest-keys', label: '설정 / Ingest 키', element: SettingsLayout, hidden: true, activePath: 'settings/orchestrators' },
    ],
  },
  {
    key: 'ai-usage',
    label: 'Slack 봇 AI 사용량',
    glyph: '◉',
    basePath: '/ai-usage',
    menuKey: 'ai-usage',
    items: [{ path: '', label: '요청별 사용 내역', element: AiUsageView }],
  },
  {
    key: 'onboarding',
    label: '온보딩',
    glyph: '◈',
    basePath: '/onboarding',
    menuKey: 'onboarding',
    items: [
      { path: '', label: '시작하기', element: StartView },
      { path: 'links', label: '링크 모음', element: LinksView },
      { path: 'setup', label: '개발 환경', element: SetupView },
      { path: 'checklist', label: '체크리스트', element: ChecklistView },
      { path: 'harness', label: '하네스 워크플로', element: HarnessView },
      { path: 'glossary', label: '용어 · 저장소', element: GlossaryView },
    ],
  },
  {
    key: 'service-dashboard',
    label: 'service-dashboard',
    glyph: '◎',
    basePath: '/service-dashboard',
    menuKey: 'service-dashboard',
    items: [
      { path: 'grafana', label: '그라파나 대시보드', element: GrafanaDashboardView },
      { path: 'service-map', label: '서비스맵', element: ServiceMapView },
    ],
  },
  {
    // 서로 다른 기능을 한 계정 관리 메뉴로 모은다. 라우트 접두어도 계정 화면으로 통일한다.
    key: 'external-account',
    label: '계정',
    glyph: '◇',
    basePath: '/accounts',
    menuKey: 'accounts',
    items: [
      { path: 'external', label: '외부 서비스 계정 관리', element: ExternalAccountView, menuKey: 'external-account' },
      { path: 'internal-infra', label: '인프라 계정 관리', element: InternalInfraAccountView, menuKey: 'internal-infra-account' },
      { path: 'console', label: '콘솔 계정 관리', element: AccountView, menuKey: 'account:console' },
    ],
  },
  {
    key: 'howto',
    label: 'how to',
    glyph: '✎',
    basePath: '/howto',
    menuKey: 'howto',
    items: [{ path: '', label: '문서', element: HowtoView }],
  },
  // 설정은 맨 아래다 — 매일 여는 화면이 아니라 필요할 때 찾아 들어가는 자리다.
  {
    key: 'settings',
    label: '설정',
    glyph: '⚙',
    basePath: '/settings',
    menuKey: 'settings',
    items: [
      { path: 'api-tokens', label: 'API 토큰', element: ApiTokenView, menuKey: 'settings:api-tokens' },
    ],
  },
];

/**
 * 메뉴 권한 — 섹션 키는 그 섹션 화면 전부를, 화면 키는 그 화면 하나를 연다. `menus`가 null이면 로그인을 강제하지
 * 않는 상태라 전부 열린다. API 쪽 같은 판정은 `account`의 접근 정책 표(`console-access.policy.ts`)다.
 */
export class NavAccess {
  static allows(menus: readonly string[] | null, section: NavSection, item: NavItem): boolean {
    if (!menus) return true;
    return menus.includes(section.menuKey) || (item.menuKey !== undefined && menus.includes(item.menuKey));
  }

  static sections(menus: readonly string[] | null): NavSection[] {
    return NAV_SECTIONS.map((section) => ({ ...section, items: section.items.filter((item) => NavAccess.allows(menus, section, item)) }))
      .filter((section) => section.items.length > 0);
  }

  /** 들어갈 수 있는 첫 화면 — 첫 섹션 권한이 없는 계정이 빈 화면에 떨어지지 않게. */
  static firstPath(menus: readonly string[] | null): string | null {
    const section = NavAccess.sections(menus).find((candidate) => candidate.items.some((item) => !item.hidden));
    const item = section?.items.find((candidate) => !candidate.hidden);
    return section && item ? NavRoute.pathOf(section, item) : null;
  }
}

export class NavRoute {
  static pathOf(section: NavSection, item: NavItem): string {
    return item.path ? `${section.basePath}/${item.path}` : section.basePath;
  }

  static find(pathname: string): { section: NavSection; item: NavItem } | null {
    for (const section of NAV_SECTIONS) {
      for (const item of section.items) {
        if (matchPath({ path: NavRoute.pathOf(section, item), end: true }, pathname)) return { section, item };
      }
    }
    return null;
  }
}
